import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useLocale } from '../context/LocaleContext';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import { getTraditionalSeventhStepPrayer } from '../content/traditionalSeventhStepPrayer';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getCongratsHeading,
  getGuidanceModalTitle,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
  getVideoGuideTitle,
} from '../config/stepPageCopy';

interface Step7Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step7: React.FC<Step7Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { t } = useLocale();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(7, 1),
      description: getStepActionDescription(7, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: getStepActionLabel(7, 2),
      description: getStepActionDescription(7, 2),
      completed: false,
      buttons: [
        {
          text: STEP_UI.READ_GUIDANCE,
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: STEP_UI.WATCH_VIDEO,
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: getStepActionLabel(7, 3),
      description: getStepActionDescription(7, 3),
      completed: false
    },
    {
      id: 4,
      title: getStepActionLabel(7, 4),
      description: getStepActionDescription(7, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(7, 5),
      description: getStepActionDescription(7, 5),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [prayerText, setPrayerText] = useState<string>('');
  const [savingPrayer, setSavingPrayer] = useState(false);
  const [savedPrayer, setSavedPrayer] = useState(false);
  const [savePrayerError, setSavePrayerError] = useState(false);
  const prayerSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prayerTextRef = useRef<string>('');
  const actionsRef = useRef<ActionItem[]>([]);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(
    () => ({ step7Prayer: prayerText }),
    [prayerText]
  );
  const { openShareDialog, shareDialogProps } = useStepShareDialog(7, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(7);

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step7', 'refreshStepData start');
      const { steps, journeyProgress } = await perfTimeAsync('Step7 refreshStepData (total)', () =>
        progressService.loadStepPagesProgress('SAA', '12-steps', [7], { includeJourney: true })
      );

      const existingProgress = steps[7] ?? null;

      if (journeyProgress) {
        setIsOptedOut(journeyProgress.opted_out);
      }

      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);

        if (savedData.actions) {
          const merged = mergeStepActions(7, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 7,
              step_title: 'Step Seven',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
            });
          }
        }

        if (savedData.prayer_text) {
          setPrayerText(savedData.prayer_text);
        }
      }

      clearError();
      perfSummary('Step Seven page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  // Keep refs in sync for auto-save (avoids stale closure overwriting other data)
  prayerTextRef.current = prayerText;
  actionsRef.current = actions;

  // Perform the actual prayer save - uses refs to get latest actions
  const savePrayerToApi = async (textToSave: string) => {
    if (!user) return;
    setSavingPrayer(true);
    setSavePrayerError(false);
    setSavedPrayer(false);
    try {
      const currentActions = actionsRef.current;
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 7,
        step_title: 'Step Seven',
        completed: currentActions.every(a => a.completed),
        data: stepDataWithActions(currentActions, getStepActionSchema(7).actionSchemaVersion, {
          prayer_text: textToSave
        })
      });
      setSavingPrayer(false);
      setSavedPrayer(true);
      setTimeout(() => setSavedPrayer(false), 2000);
    } catch (err: any) {
      console.error('Error auto-saving prayer:', err);
      setSavingPrayer(false);
      setSavePrayerError(true);
      setTimeout(() => setSavePrayerError(false), 3000);
      handleError(err, t('step.workspace.save_prayer_error'));
    }
  };

  // Auto-save prayer text with debouncing
  const autoSavePrayer = (newPrayerText?: string) => {
    if (prayerSaveTimeoutRef.current) {
      clearTimeout(prayerSaveTimeoutRef.current);
    }
    prayerSaveTimeoutRef.current = setTimeout(() => {
      const textToSave = newPrayerText !== undefined ? newPrayerText : prayerTextRef.current;
      savePrayerToApi(textToSave);
    }, 300);
  };

  // Cleanup prayer save timeout on unmount
  useEffect(() => {
    return () => {
      if (prayerSaveTimeoutRef.current) {
        clearTimeout(prayerSaveTimeoutRef.current);
      }
    };
  }, []);

  // Save prayer when user switches tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (prayerSaveTimeoutRef.current) {
          clearTimeout(prayerSaveTimeoutRef.current);
          prayerSaveTimeoutRef.current = null;
        }
        const textToSave = prayerTextRef.current;
        if (textToSave && user) {
          savePrayerToApi(textToSave);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 7,
        step_title: 'Step Seven',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(7).actionSchemaVersion, {
          prayer_text: prayerText
        })
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Seven - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step7_completed' : 'step7_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(7)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={7}
                stepTitle={getStepPageTitle(7)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(7)}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <div className={styles.actionsHeader}>
              <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(7)}</h2>
            </div>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>
            
            <StepActions
              actions={withShareButton(7, actions, openShareDialog)}
              onActionChange={handleActionChange}
              stepNumber={7}
              renderActionContent={(action) => {
                if (action.id === 3) {
                  return (
                    <div className={styles.actionContentIndent}>
                      <div style={{ marginBottom: 16, width: '100%' }}>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 8, 
                          padding: 20, 
                          marginBottom: 20,
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          color: '#333'
                        }}>
                          <h4 style={{ 
                            margin: '0 0 12px 0', 
                            color: '#333', 
                            fontStyle: 'normal',
                            fontWeight: 600
                          }}>
                            {t('step.7.prayer.traditional_heading')}
                          </h4>
                          <p style={{ margin: '0 0 4px 0', paddingLeft: '20px' }}>
                            {getTraditionalSeventhStepPrayer()}
                          </p>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ 
                            display: 'block', 
                            fontWeight: 500, 
                            color: '#333', 
                            marginBottom: 8 
                          }}>
                            {t('step.7.prayer.own_words_label')}
                          </label>
                          <textarea
                            value={prayerText}
                            onChange={e => {
                              const val = e.target.value;
                              setPrayerText(val);
                              prayerTextRef.current = val;
                              autoSavePrayer(val);
                            }}
                            onBlur={() => autoSavePrayer()}
                            rows={8}
                            style={{ 
                              width: '100%', 
                              borderRadius: 4, 
                              border: '1px solid #ccc', 
                              padding: 8, 
                              boxSizing: 'border-box',
                              resize: 'vertical'
                            }}
                            placeholder={t('step.7.prayer.own_words_placeholder')}
                          />
                          <div style={{ 
                            textAlign: 'right', 
                            marginTop: 8, 
                            minHeight: 24,
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            {savingPrayer && (
                              <span style={{ color: '#6c757d' }}>{t('step.workspace.saving')}</span>
                            )}
                            {savedPrayer && (
                              <span style={{ color: '#28a745' }}>{t('step.workspace.saved')}</span>
                            )}
                            {savePrayerError && (
                              <span style={{ color: '#dc3545' }}>{t('step.workspace.save_failed')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getCongratsHeading()}</h3>
                  <p>{getCongratsBody(7)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-6" className={styles.prevStepLink}>
                      {getPrevStepNavLabel(7)}
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-8" className={styles.nextStepLink}>
                      {getNextStepNavLabel(7)}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-6" className={styles.prevStepLink}>
                    {getPrevStepNavLabel(7)}
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-8" className={styles.nextStepLink}>
                    {getNextStepNavLabel(7)}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-seven start>>"
        endTag="<<step-seven end>>"
      />

      {/* ContentReader for How to Work Step Seven */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_7_Guidance.md"
        startTag="<<working-step-seven start>>"
        endTag="<<working-step-seven end>>"
        title={getGuidanceModalTitle(7)}
      />

      {/* Video Modal for Step Seven */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="eGgHCYElqDw"
        title={getVideoGuideTitle(7)}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={7}
        stepTitle={getStepPageTitle(7)}
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step7; 
